import { isToday } from 'date-fns';
import { customAlphabet } from 'nanoid';
import { IConfig } from '../../config';
import { Account } from '../../state';
import {
  CurrencyCross,
  getInstrumentId,
  InstrumentCategoryEnum,
  Inventory,
  InventoryUI,
  InventoryWithListedInstrument,
  isRealTimeInstrumentCategory,
  ListedInstrument,
} from '../../types';
import { getDateFromDateTime, isDefined, NbLogger } from '../../utils';

const allowedCharacters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
export const htmlNanoId = customAlphabet(allowedCharacters, 10);
const notRealTimeCategories = [InstrumentCategoryEnum.Futures, InstrumentCategoryEnum.Option];

/**
 * Returns the weight of an inventory item
 * @param inventory Inventory item
 * @private
 */
export const getInventoryInitPriceValue = (inventory: Inventory): number => {
  return inventory.initPriceValue;
};

export const getInventoryListByAccountList = <T extends Inventory>(
  inventories: Array<T>,
  accountsOrAccountNumber: string | Array<Account>,
): Array<T> => {
  const accountNumbers = Array.isArray(accountsOrAccountNumber)
    ? accountsOrAccountNumber.map(_ => _.accountNumber)
    : [accountsOrAccountNumber];
  return inventories.filter(_ => accountNumbers.includes(_.accountNumber));
};

export const isNotRealTimeInventory = (inventoryOrCategory: Inventory | InstrumentCategoryEnum) => {
  const category =
    typeof inventoryOrCategory === 'string'
      ? inventoryOrCategory
      : (inventoryOrCategory.instrumentCategory as InstrumentCategoryEnum);
  return notRealTimeCategories.includes(category);
};

const calculatePnlDailyPercentage = (marketPriceSingle: number, closePrice?: number) => {
  if (!closePrice) {
    return undefined;
  }

  if (marketPriceSingle - closePrice === 0) {
    return 0;
  }

  return marketPriceSingle / closePrice;
};

const calculatePnlDaily = ({
  marketPriceSingle,
  closePrice,
  position,
  rate,
  inventoryDate: inventoryDateStr,
  pNl,
}: {
  marketPriceSingle: number;
  closePrice?: number;
  position: number;
  rate: number;
  pNl?: number;
  inventoryDate?: string | null;
}): number | undefined => {
  const inventoryDate = inventoryDateStr ? getDateFromDateTime(inventoryDateStr) : undefined;
  if (inventoryDate && isToday(inventoryDate)) {
    return pNl;
  }

  if (!closePrice) {
    return undefined;
  }

  return (marketPriceSingle - closePrice) * position * rate;
};
/**
 * Returns the initial PnL and PnL percentage of an inventory like item
 * !! If closePrice is provided, it calculates the daily PnL and daily PnL percentage !!
 * @param props
 */
export const getInitialPNl = (props: {
  instrumentCategory: InstrumentCategoryEnum;
  position: number;
  marketPriceSingle: number;
  initPriceValue: number;
  rate: number;
  grossProfitLoss?: number;
  closePrice?: number;
  ric?: string;
  inventoryDate?: string | null;
}): { pNL?: number; pNLPercentage?: number; pNLDaily?: number; pNlDailyPercentage?: number } => {
  const { instrumentCategory, position, marketPriceSingle, initPriceValue, grossProfitLoss, closePrice, ric, rate } =
    props;
  if (isDefined(ric) && isRealTimeInstrumentCategory(instrumentCategory)) {
    if (marketPriceSingle === 0) {
      return { pNL: undefined, pNLPercentage: undefined };
    }
    const pNl = marketPriceSingle * position * rate - initPriceValue;
    const pNlPercentage = initPriceValue === 0 ? undefined : pNl / initPriceValue;
    const pNlDaily = calculatePnlDaily({
      marketPriceSingle,
      closePrice,
      position,
      rate,
      inventoryDate: props.inventoryDate,
      pNl,
    });
    const pNlDailyPercentage = calculatePnlDailyPercentage(marketPriceSingle, closePrice);

    return { pNL: pNl, pNLPercentage: pNlPercentage, pNLDaily: pNlDaily, pNlDailyPercentage };
  } else {
    if (!isDefined(grossProfitLoss) && isNotRealTimeInventory(instrumentCategory)) {
      return { pNL: undefined, pNLPercentage: undefined };
    }

    if (initPriceValue === 0) {
      return {
        pNL: grossProfitLoss,
        pNLPercentage: undefined,
      };
    }

    return {
      pNL: grossProfitLoss,
      pNLPercentage: isDefined(grossProfitLoss) ? grossProfitLoss / initPriceValue : undefined,
    };
  }
};

/**
 * Creates a unique id for an inventory item, because backend does not provide one
 *  - for currencies we use ric+isin+mic+instrumentName
 *  - for other instruments we use ric+isin+mic+instrumentName PLUS short or long
 */
export const getInventoryId = (inventory: Omit<Inventory, keyof InventoryUI>) => {
  if (inventory.instrumentCategory === InstrumentCategoryEnum.Currency) {
    return `${inventory.ric || ''}-${inventory.isin || ''}-${inventory.marketMICCode || ''}-${
      inventory.instrumentName
    }-${inventory.initCcy}`;
  } else {
    return `${inventory.ric || ''}-${inventory.isin || ''}-${inventory.marketMICCode || ''}-${
      inventory.instrumentName
    }-${inventory.position < 0 ? 'short' : 'long'}-${inventory.initCcy}`;
  }
};

export type TradableInventorList = {
  tradableInventories: Array<InventoryWithListedInstrument>;
  incorrectInventories: Array<Inventory>;
};
/**
 * Creates the tradable inventory list for the inventory details.
 * We have this method because loaded instruments are merged with the Inventory items
 */
export const getTradableInventoryList = (
  inventoryInstrumentRics: Array<string>,
  inventoryItemsGroupedByInstrument: Record<string, Array<Inventory>>,
  instrumentList: Array<ListedInstrument>,
  tradableInventoriesWithoutInstrument: Array<Inventory>,
): TradableInventorList => {
  if (inventoryInstrumentRics.length === 0 || !instrumentList) {
    return { tradableInventories: [], incorrectInventories: [] };
  }
  return Object.keys(inventoryItemsGroupedByInstrument).reduce(
    (res, ric) => {
      const instrument = instrumentList.find(_ => _.ric === ric);
      const inventoriesByRic = tradableInventoriesWithoutInstrument.filter(_ => _.ric === ric);
      if (instrument && inventoriesByRic.length > 0) {
        inventoriesByRic.forEach(inventory => {
          if (inventory.isIncorrect) {
            res.incorrectInventories.push({
              ...inventory,
              ric: undefined,
            });
          } else {
            res.tradableInventories.push({
              ...inventory,
              instrument: instrument,
              ric: ric,
            });
          }
        });
      }
      return res;
    },
    { tradableInventories: [], incorrectInventories: [] } as TradableInventorList,
  );
};

/**
 * Updates the market price of an inventory item.
 * This function handles the edge cases when the last price currency is different from the initial currency.
 *
 * @param inventory Inventory item
 * @param logger Logger instance
 * @param config Config instance
 * @param exchangeRates Exchange rates
 * @param latestPriceCcy Last price currency
 * @param latestPrice Last price
 */
export const updateInventoryMarketPrice = ({
  inventory,
  logger,
  config,
  exchangeRates,
  latestPriceCcy,
  latestPrice,
}: {
  inventory: Inventory;
  logger: NbLogger;
  config: IConfig;
  exchangeRates: Map<CurrencyCross, number>;
  latestPriceCcy: string;
  latestPrice: number;
}): void => {
  const instrumentId = getInstrumentId(inventory);
  if (!instrumentId) {
    throw new Error(`Failed to get instrument id for inventory (${inventory.id})`);
  }

  const { marketPriceCcy, initCcy } = inventory;

  if (latestPriceCcy === initCcy && marketPriceCcy !== initCcy) {
    // convert market price and ecxchange rate to be in initCcy
    //  -- no need for this -- only if the market price currency is different from the initial currency
    const marketCcyInitCcyExchangeRate =
      latestPriceCcy !== initCcy && marketPriceCcy !== initCcy
        ? exchangeRates.get(`${marketPriceCcy}${initCcy}=` as CurrencyCross)
        : 1;
    if (!marketCcyInitCcyExchangeRate) {
      logger.error(`Failed to get exchange rate for ${marketPriceCcy}${initCcy}= (${instrumentId})`);
      throw new Error(`Failed to get exchange rate for ${marketPriceCcy}${initCcy}= (${instrumentId})`);
    }

    // set market price
    inventory.marketPriceSingle = latestPrice * marketCcyInitCcyExchangeRate;
    inventory.marketPrice = inventory.marketPriceSingle * inventory.position;
    inventory.marketPriceCcy = latestPriceCcy;
  } else if (latestPriceCcy !== initCcy && marketPriceCcy !== initCcy) {
    // convert market price and exchange rate to be in initCcy
    const latestPriceInitCcyExchangeRate = exchangeRates.get(`${latestPriceCcy}${initCcy}=` as CurrencyCross);
    if (!latestPriceInitCcyExchangeRate) {
      logger.error(`Failed to get exchange rate for ${latestPriceCcy}${initCcy}= (${instrumentId})`);
      throw new Error(`Failed to get exchange rate for ${latestPriceCcy}${initCcy}= (${instrumentId})`);
    }

    // set market price
    inventory.marketPriceSingle = latestPrice * latestPriceInitCcyExchangeRate;
    inventory.marketPrice = inventory.marketPriceSingle * inventory.position;
    inventory.marketPriceCcy = initCcy;
  } else if (latestPriceCcy === marketPriceCcy) {
    inventory.marketPriceSingle = latestPrice;
  } else if (latestPriceCcy !== initCcy && marketPriceCcy === initCcy) {
    // convert latest price to be in initCcy
    const latestPriceInitCcyExchangeRate = exchangeRates.get(`${latestPriceCcy}${initCcy}=` as CurrencyCross);
    if (!latestPriceInitCcyExchangeRate) {
      logger.error(`Failed to get exchange rate for ${latestPriceCcy}${initCcy}= (${instrumentId})`);
      throw new Error(`Failed to get exchange rate for ${latestPriceCcy}${initCcy}= (${instrumentId})`);
    }

    inventory.marketPriceSingle = latestPrice * latestPriceInitCcyExchangeRate;
    inventory.marketPrice = inventory.marketPriceSingle * inventory.position;
    inventory.marketPriceCcy = initCcy;
    inventory.exchangeRate = 1; // set to base currency
  }

  // set exchange rate to be in initCcy/baseCurrency
  if (initCcy !== config.NetBroker.BaseCurrency) {
    const initCcyBaseCurrencyExchangeRate = exchangeRates.get(
      `${initCcy}${config.NetBroker.BaseCurrency}=` as CurrencyCross,
    );
    if (!initCcyBaseCurrencyExchangeRate) {
      logger.error(`Failed to get exchange rate for ${initCcy}${config.NetBroker.BaseCurrency}= (${instrumentId})`);
      throw new Error(`Failed to get exchange rate for ${initCcy}${config.NetBroker.BaseCurrency}= (${instrumentId})`);
    }
    inventory.exchangeRate = initCcyBaseCurrencyExchangeRate;
  } else {
    inventory.exchangeRate = 1; // set to base currency
  }
};

export const isInventoryWithListedInstrument = (inventory: Inventory): inventory is InventoryWithListedInstrument =>
  (inventory as InventoryWithListedInstrument).instrument !== undefined;
